import { useEffect, useState } from 'react';
import { Check, Dices, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  ACCESSORIES,
  BOTTOM_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  SKIN_TONES,
  TOP_COLORS,
  seededAvatar,
  type AvatarConfig,
  type AvatarOption,
  type Facing,
} from '@/features/digitalOffice/avatars';
import { AvatarPreview } from './AvatarPreview';

interface AvatarCreatorDialogProps {
  open: boolean;
  /** Existing look when editing; `null` for first-time creation. */
  initial: AvatarConfig | null;
  /** Seed for the "random" starting point (user id). */
  seed: string;
  /** Required on first run — the dialog can't be dismissed without saving. */
  required?: boolean;
  saving?: boolean;
  onSave: (config: AvatarConfig) => void;
  onOpenChange: (open: boolean) => void;
}

const FACINGS: Facing[] = ['down', 'left', 'up', 'right'];

/**
 * "Create your character" — pick skin, hair, outfit, and an accessory
 * with a live animated preview. Modern and flat (no pixel art): swatches
 * for colours, chips for shapes, one big preview that you can spin.
 */
export function AvatarCreatorDialog({
  open,
  initial,
  seed,
  required = false,
  saving = false,
  onSave,
  onOpenChange,
}: AvatarCreatorDialogProps) {
  const [config, setConfig] = useState<AvatarConfig>(() => initial ?? seededAvatar(seed));
  const [facingIdx, setFacingIdx] = useState(0);

  // Re-seed whenever the dialog opens so an abandoned edit doesn't leak.
  useEffect(() => {
    if (open) {
      setConfig(initial ?? seededAvatar(seed));
      setFacingIdx(0);
    }
  }, [open, initial, seed]);

  const set = <K extends keyof AvatarConfig>(key: K, value: AvatarConfig[K]) =>
    setConfig((c) => ({ ...c, [key]: value }));

  const randomize = () => {
    setConfig(seededAvatar(`${seed}:${Date.now()}:${Math.random()}`));
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && required) {
      toast.message('Pick a character to enter the office.');
      return;
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-1.5rem)] max-w-2xl flex-col overflow-hidden"
        onInteractOutside={(e) => {
          if (required) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (required) e.preventDefault();
        }}
      >
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle>{initial ? 'Edit your character' : 'Create your character'}</DialogTitle>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto sm:grid-cols-[200px_1fr]">
          {/* Preview column */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setFacingIdx((i) => (i + 1) % FACINGS.length)}
              title="Click to rotate"
              className="relative flex h-[200px] w-[200px] items-center justify-center overflow-hidden rounded-2xl border border-border bg-[radial-gradient(ellipse_at_center,_#F3F4F0_0%,_#E6E8E2_100%)] transition-colors hover:border-old-growth/40"
            >
              <AvatarPreview config={config} size={180} facing={FACINGS[facingIdx]} animate />
              <span className="absolute bottom-2 right-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                tap to rotate
              </span>
            </button>
            <Button type="button" variant="outline" size="sm" onClick={randomize}>
              <Dices className="h-3.5 w-3.5" />
              Surprise me
            </Button>
          </div>

          {/* Options column */}
          <div className="space-y-4">
            <SwatchRow
              label="Skin"
              options={SKIN_TONES}
              value={config.skin_tone}
              onChange={(v) => set('skin_tone', v)}
            />
            <ChipRow
              label="Hair"
              options={HAIR_STYLES}
              value={config.hair_style}
              onChange={(v) => set('hair_style', v)}
            />
            <SwatchRow
              label="Hair colour"
              options={HAIR_COLORS}
              value={config.hair_color}
              onChange={(v) => set('hair_color', v)}
              disabled={config.hair_style === 'none'}
            />
            <SwatchRow
              label="Top"
              options={TOP_COLORS}
              value={config.top_color}
              onChange={(v) => set('top_color', v)}
            />
            <SwatchRow
              label="Bottoms"
              options={BOTTOM_COLORS}
              value={config.bottom_color}
              onChange={(v) => set('bottom_color', v)}
            />
            <ChipRow
              label="Accessory"
              options={ACCESSORIES}
              value={config.accessory}
              onChange={(v) => set('accessory', v)}
            />
          </div>
        </div>

        <DialogFooter className="shrink-0">
          {!required && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Button type="button" onClick={() => onSave(config)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {initial ? 'Save changes' : 'Enter the office'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwatchRow({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: AvatarOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={cn(disabled && 'pointer-events-none opacity-40')}>
      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const active = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={active}
              title={o.label}
              onClick={() => onChange(o.id)}
              className={cn(
                'relative h-8 w-8 rounded-full border transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active ? 'border-humus ring-2 ring-humus/30' : 'border-black/10',
              )}
              style={{ backgroundColor: o.hex }}
            >
              {active && (
                <Check
                  className={cn(
                    'absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2',
                    isDark(o.hex ?? '#ffffff') ? 'text-white' : 'text-humus',
                  )}
                  strokeWidth={2.5}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChipRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: AvatarOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const active = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(o.id)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-humus bg-humus text-white'
                  : 'border-border bg-background text-foreground hover:bg-secondary',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function isDark(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}
